import { FC, Suspense } from 'react';
import { lazily } from 'react-lazily';
import { createBrowserRouter, RouterProvider } from 'react-router-dom';

import { Box } from '@bonusly/design-system';
import { assertPresence } from '@bonusly/runtime';

import { AdminAppLayout } from '../components/layout/AdminAppLayout';
import { AppRootLayout } from '../components/layout/AppRootLayout';
import { FlaggedRoute } from '../components/routes/FlaggedRoute';
import { RequireAdmin } from '../components/routes/require-admin';
import { RouteError } from '../components/routes/RouteError';
import { MobileUnavailable } from '../components/routes/statuses/MobileUnavailable';
import { PageNotFound } from '../components/routes/statuses/PageNotFound';
import { loadRecognitionScoresData } from '../modules/reports/recognition-scores/loaders';
import { loadRewardsHistory, loadRewardsIndex } from '../modules/rewards/loaders';
import { loadAward } from '../queries/awards';
import { loadCelebration, loadCelebrationMetadata, loadInactiveCelebrations } from '../queries/celebrations';
import { loadCustomPropertiesUserGroups, loadUserGroups } from '../queries/groups';
import { loadAdminIncentive } from '../queries/incentives';
import { loadAdminNominationPeriod } from '../queries/nominations';
import { loadSubscriptions } from '../queries/subscriptions';

// Lazily loaded module collections. These are grouped to reduce the number of HTTP requests on app load
const Admin = lazily(() => import('../modules/admin'));
const Awards = lazily(() => import('../modules/awards'));
const Celebrations = lazily(() => import('../modules/celebrations'));
const Growth = lazily(() => import('../modules/growth'));
const User = lazily(() => import('../modules/user'));
const Incentives = lazily(() => import('../modules/incentives'));
const Nominations = lazily(() => import('../modules/nominations'));
const Recognition = lazily(() => import('../modules/recognition'));
const Reports = lazily(() => import('../modules/reports'));
const Rewards = lazily(() => import('../modules/rewards'));
const SuperAdmin = lazily(() => import('../modules/super-admin'));

// Widget components - lazy loaded to avoid bloating main bundle
const { WidgetLayout } = lazily(() => import('../components/layout/widget-layout'));
const { WidgetPage } = lazily(() => import('../components/widget/widget-page'));

const router = createBrowserRouter([
	{
		path: 'admin',
		element: <AdminAppLayout />,
		errorElement: <RouteError />,
		children: [
			{
				path: 'company/*',
				// TODO: Can we update the layout so we don't need to repeat <RouteError /> here?
				errorElement: <RouteError />,
				element: <Admin.Company />,
			},
			{
				path: 'dashboard/*',
				errorElement: <RouteError />,
				element: <Admin.Dashboard />,
			},
			{
				path: 'growth/*',
				errorElement: <RouteError />,
				element: <Admin.Growth />,
			},
			{
				path: 'incentives/*',
				errorElement: <RouteError />,
				element: <Incentives.IncentivesSummary />,
				children: [
					{
						path: 'summary',
						element: <Incentives.IncentivesSummary />,
					},
				],
			},
			{
				path: 'subscriptions',
				errorElement: <RouteError />,
				element: <Admin.ReportSubscriptions />,
				loader: () => loadSubscriptions(),
			},
			{
				path: 'reports/participation',
				errorElement: <RouteError />,
				element: <Reports.ParticipationReport />,
				children: [
					{
						path: 'giving-and-receiving',
						element: <Reports.ParticipationReport />,
					},
					{
						path: 'managers-and-teams',
						element: <Reports.ParticipationReport />,
					},
					{
						path: 'compare',
						element: <Reports.ParticipationReport />,
					},
				],
			},
			{
				path: 'programs/awards',
				element: <Awards.AwardsRoot />,
				errorElement: <RouteError />,
				children: [
					{
						path: '',
						element: <Awards.AwardsIndex />,
						loader: () => {
							return loadCustomPropertiesUserGroups();
						},
					},
					{
						path: 'new',
						element: <Awards.CreateAward />,
						loader: () => {
							return loadCustomPropertiesUserGroups();
						},
					},
					{
						path: ':id/edit',
						element: <Awards.EditAward />,
						loader: ({ params }) => {
							assertPresence(params.id);
							return Promise.all([loadAward(params.id), loadCustomPropertiesUserGroups()]);
						},
					},
					{
						path: 'archived',
						element: <Awards.AwardsArchivedIndex />,
						loader: () => {
							return loadCustomPropertiesUserGroups();
						},
					},
				],
			},
			{
				path: 'programs/celebrations',
				element: <Celebrations.CelebrationsRoot />,
				errorElement: <RouteError />,
				children: [
					{
						path: '',
						element: <Celebrations.CelebrationsIndex />,
						loader: () => {
							return loadInactiveCelebrations();
						},
					},
					{
						path: 'new/:type',
						element: <Celebrations.CreateCelebration />,
						loader: ({ params }) => {
							assertPresence(params.type);
							return Promise.all([loadUserGroups(), loadCelebrationMetadata({ celebration_type: params.type })]);
						},
					},
					{
						path: ':id/edit',
						element: <Celebrations.EditCelebration />,
						loader: ({ params }) => {
							assertPresence(params.id);
							return Promise.all([loadCelebration(params.id), loadUserGroups(), loadCelebrationMetadata({ celebration_id: params.id })]);
						},
					},
				],
			},
			{
				path: 'programs/nominations',
				element: <Nominations.NominationsRoot />,
				errorElement: <RouteError />,
				children: [
					{
						path: '',
						element: <Nominations.NominationPeriodsIndex />,
					},
					{
						path: 'new',
						element: <Nominations.CreateNominationPeriod />,
						loader: () => loadUserGroups(),
					},
					{
						path: ':id',
						element: <Nominations.AdminNominationPeriod />,
						loader: ({ params }) => {
							assertPresence(params.id);
							return loadAdminNominationPeriod(params.id);
						},
					},
					{
						path: ':id/edit',
						element: <Nominations.EditNominationPeriod />,
						loader: ({ params }) => {
							assertPresence(params.id);
							return Promise.all([loadAdminNominationPeriod(params.id), loadUserGroups()]);
						},
					},
				],
			},
			{
				path: 'programs/incentives',
				element: <Incentives.IncentivesRoot />,
				errorElement: <RouteError />,
				children: [
					{
						path: '',
						element: <Incentives.IncentivesIndex />,
						loader: () => {
							return loadCustomPropertiesUserGroups();
						},
					},
					{
						path: 'new',
						element: <Incentives.CreateIncentive />,
						loader: () => {
							return loadCustomPropertiesUserGroups();
						},
					},
					{
						path: ':id/edit',
						element: <Incentives.EditIncentive />,
						loader: ({ params }) => {
							assertPresence(params.id);
							return Promise.all([loadAdminIncentive(params.id), loadCustomPropertiesUserGroups()]);
						},
					},
					{
						path: 'archived',
						element: <Incentives.IncentivesArchivedIndex />,
						loader: () => {
							return loadCustomPropertiesUserGroups();
						},
					},
				],
			},
			{
				path: 'programs/*',
				errorElement: <RouteError />,
				element: <Admin.Programs />,
			},
			{
				path: 'reports/recognition_scores',
				errorElement: <RouteError />,
				element: (
					<RequireAdmin require="finance">
						<Reports.RecognitionScores />
					</RequireAdmin>
				),
				loader: loadRecognitionScoresData,
			},
			{
				path: 'reports/pulse-results',
				errorElement: <RouteError />,
				element: (
					<RequireAdmin require={['finance', 'global']}>
						<Reports.CheckInsResults />
					</RequireAdmin>
				),
			},
			{
				path: 'reports/points-activity',
				errorElement: <RouteError />,
				element: (
					<RequireAdmin require={['finance', 'global']}>
						<Reports.PointsActivityReport />
					</RequireAdmin>
				),
				children: [
					{
						path: 'summary',
						element: <Reports.PointsActivityReport />,
					},
					{
						path: 'programs',
						element: <Reports.PointsActivityReport />,
					},
					{
						path: 'reward-balance',
						element: <Reports.PointsActivityReport />,
					},
				],
			},
			{
				path: 'rewards/*',
				errorElement: <RouteError />,
				element: (
					<RequireAdmin require="rewards">
						<Admin.Rewards />
					</RequireAdmin>
				),
			},
			{
				path: '*',
				element: (
					<Box p={6}>
						<PageNotFound />
					</Box>
				),
			},
		],
	},
	// Super Admin routes
	{
		path: 'super_admin',
		element: <SuperAdmin.SuperAdminAppLayout />,
		errorElement: <RouteError />,
		children: [
			{
				path: 'kvms/recognition/giving-rate',
				element: <SuperAdmin.RecognitionGivingRate />,
			},
			{
				path: 'kvms/one-on-one',
				element: <SuperAdmin.OneOnOneKvms />,
			},
			{
				path: 'kvms/pulse-checks',
				element: <SuperAdmin.PulseChecksKvms />,
			},
		],
	},
	// Print-friendly and shared pages do not have a navbar/layout
	{
		path: '/p',
		children: [
			{
				path: 'recap-review/*',
				errorElement: <RouteError />,
				element: (
					<Suspense>
						<Growth.RecapReviewPrintView />
					</Suspense>
				),
			},
		],
	},
	// Widget page for iframe embedding
	{
		path: '/widget',
		element: <WidgetLayout />,
		errorElement: <RouteError />,
		children: [
			{
				path: '',
				element: <WidgetPage />,
			},
		],
	},
	{
		path: '/share',
		children: [
			{
				path: 'nominations/:id',
				errorElement: <RouteError />,
				element: (
					<Suspense>
						<Nominations.PublicNominationPeriod />
					</Suspense>
				),
				loader: ({ params }) => {
					assertPresence(params.id);
					return loadAdminNominationPeriod(params.id);
				},
			},
		],
	},
	{
		path: '/',
		element: <AppRootLayout />,
		errorElement: <RouteError />,
		children: [
			{
				path: '/',
				errorElement: <RouteError />,
				element: <Recognition.BonusFeed />,
				// Originally this was set to three different paths that each would show `Recognition.BonusFeed`.
				// What this meant, however, is that if the path changed it would totally unmount and then remount
				// the component tree. So, if you were on the home page and navigated to /invite from a button (which
				// should still show the home page) you'd see a flash, then the home page would load, followed by the
				// invite modal. This causes issues when multiple modals are already open, however, because handlers
				// and references added to the screen seem to be lost. So, what this does is just treat the three paths
				// as child routes but don't actually render anything for them. This allows the BonusFeed component to
				// remain mounted and visible while the user navigates between the different paths.
				children: [
					{
						path: '',
						element: null,
					},
					{
						path: 'invite',
						element: null,
					},
					{
						path: 'bonuses/:id',
						element: null,
					},
				],
			},
			{
				path: 'rewards/history',
				errorElement: <RouteError />,
				element: <Rewards.RewardsHistory />,
				loader: loadRewardsHistory,
			},
			{
				path: 'rewards',
				errorElement: <RouteError />,
				element: <Rewards.RewardsIndex />,
				loader: loadRewardsIndex,
				shouldRevalidate: ({ currentUrl, nextUrl }) => {
					if (!currentUrl || !nextUrl) {
						return true;
					}

					const currentPath = new URL(currentUrl).pathname;
					const nextPath = new URL(nextUrl).pathname;
					return currentPath !== nextPath;
				},
			},
			{
				path: 'bookmark',
				errorElement: <RouteError />,
				element: <Recognition.Bookmarks />,
			},
			{
				path: 'growth/*',
				errorElement: <RouteError />,
				element: <Growth.Growth />,
			},
			{
				path: 'incentives/*',
				errorElement: <RouteError />,
				element: <Incentives.IncentiveSelect />,
				children: [
					{
						path: ':id',
						element: <Incentives.IncentiveDetails />,
					},
				],
			},
			{
				path: 'team/*',
				errorElement: <RouteError />,
				element: <Growth.TeamManagement />,
			},
			{
				path: 'user-settings',
				element: (
					<FlaggedRoute variant="user-settings-redesign">
						<User.UserSettings />
					</FlaggedRoute>
				),
			},
			{
				path: 'mobile-unavailable',
				element: <MobileUnavailable />,
			},
			{
				path: '*',
				element: <PageNotFound />,
			},
		],
	},
]);

export const AppRouter: FC = () => <RouterProvider router={router} />;
